import { Box, Text } from '@opendoor/bricks-next';
import { Image, Link } from '@opendoor/bricks/core';

import { COSMOS_URL } from 'components/globals';

import { ArticleProps } from '../../cms/index';
import { getSocialLink } from '../../helpers/socialLinks';
import { PersonInline } from './Person';
import { articleTopicEnvRoot, getArticleCategory } from './util';

const ArticleCategoryBreadcrumbLink: Record<string, string> = {
  'Company News': '/company-news',
  'Home Buying Tips': '/moving-trends-insights',
  Sellers: '/sell',
  Buyers: '/buy',
  Agents: '/agent',
};

export const ArticleHeader = (props: ArticleProps) => {
  const articleCategory = getArticleCategory(props.sys.tags);

  // Only articles after the contentful migration will have a header image
  const headerImgUrl = props.fields?.headerImage?.fields.file?.url;
  const headerImgAlt =
    props.fields?.headerImage?.fields?.description || props.fields?.headerImage?.fields?.title;

  // Enumerate author/reviewer field props for readability
  const author = props.fields.authors?.[0];
  const reviewer = props.fields.reviewers?.[0];
  const {
    description: authorDescription,
    contact: authorContact,
    linkedInUsername: authorLinkedIn,
    displayName: authorDisplayName,
  } = author?.fields || {};
  const {
    description: reviewerDescription,
    contact: reviewerContact,
    linkedInUsername: reviewerLinkedIn,
    displayName: reviewerDisplayName,
  } = reviewer?.fields || {};

  return (
    <>
      <Box
        marginBottom="$32x"
        $largerThanSM={{
          marginBottom: 90,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          columnGap: '$12x',
        }}
      >
        <Box
          $largerThanSM={{
            flexGrow: 12,
            flexShrink: 1,
            flexBasis: 12,
          }}
        >
          <Text
            tag="p"
            color="$contentSecondary"
            typography="$labelMedium"
            marginBottom="$12x"
            display="flex"
            alignItems="center"
            columnGap="$3x"
          >
            <Link
              href={COSMOS_URL}
              aria-label="Home"
              analyticsName="article-header-breadcrumb-home"
            >
              Home
            </Link>
            <Text tag="span" typography="$labelMedium" color="$contentSecondary">
              &gt;
            </Text>
            <Link
              href={COSMOS_URL + '/newsroom'}
              aria-label="Newsroom"
              analyticsName="article-header-breadcrumb-newsroom"
            >
              Newsroom
            </Link>
            <Text tag="span" typography="$labelMedium" color="$contentSecondary">
              &gt;
            </Text>
            {ArticleCategoryBreadcrumbLink?.[articleCategory] ? (
              <Link
                href={articleTopicEnvRoot + ArticleCategoryBreadcrumbLink[articleCategory]}
                aria-label={articleCategory}
                analyticsName="article-header-breadcrumb-article-category"
              >
                {articleCategory}
              </Link>
            ) : (
              <Text tag="span" typography="$labelMedium" color="$contentSecondary">
                {articleCategory}
              </Text>
            )}
          </Text>
          <Text tag="h1" typography="$headerLarge" $largerThanSM={{ typography: '$displayMedium' }}>
            {props.fields.articleName}
          </Text>
        </Box>
        <Box
          paddingLeft="$36x"
          display="none"
          $largerThanSM={{
            display: 'block',
          }}
        >
          {!props.fields.hideReadingTime && (
            <Text tag="p" color="$contentSecondary" typography="$labelMedium" marginBottom="$20x">
              Reading Time &mdash; {props.readingTimeMinutes} minutes
            </Text>
          )}
          {props.fields.publishDate && (
            <Text tag="p" color="$contentSecondary" typography="$bodyMedium" marginBottom="$4x">
              Publication date:{' '}
              {new Date(props.fields.publishDate + 'T00:00:00').toLocaleDateString('en-us', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
          )}
          {props.fields.actualizationDate && (
            <Text tag="p" color="$contentSecondary" typography="$bodyMedium">
              Actualization Date:{' '}
              {new Date(props.fields.actualizationDate + 'T00:00:00').toLocaleDateString('en-us', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
          )}
        </Box>
      </Box>
      <Box
        $largerThanSM={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          columnGap: '$16x',
        }}
        marginBottom={
          props.fields.keyTakeaways && props.fields.keyTakeaways?.content.length > 0 ? '$32x' : 0
        }
      >
        <Box
          marginBottom="$32x"
          $largerThanSM={{
            marginBottom: 0,
            flexGrow: 4,
            flexShrink: 1,
            flexBasis: '20%',
          }}
        >
          {props.fields.articleSubtitle && (
            <Text tag="p" typography="$headerSmall" marginBottom="$8x">
              {props.fields.articleSubtitle}
            </Text>
          )}
          {author && (
            <Box marginBottom="$4x">
              <Text tag="p" typography="$labelMedium" color="$contentSecondary" marginBottom="$2x">
                Author
              </Text>
              <PersonInline person={author} />
            </Box>
          )}
          {authorDescription && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginBottom="$4x">
              <Text tag="span" typography="$bodyMedium">
                {authorDescription}
              </Text>
            </Text>
          )}
          {authorContact && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginBottom="$4x">
              <Text tag="span" typography="$labelMedium">
                Contact:&nbsp;
              </Text>
              <Link
                href={`mailto:${authorContact}`}
                aria-label={`Email ${authorDisplayName || 'author'}`}
                analyticsName="article-header-author-email"
              >
                {authorContact}
              </Link>
            </Text>
          )}
          {reviewer && (
            <Box marginTop="$8x" marginBottom="$4x">
              <Text tag="p" typography="$labelMedium" color="$contentSecondary" marginBottom="$2x">
                Reviewer
              </Text>
              <PersonInline person={reviewer} />
            </Box>
          )}
          {reviewerDescription && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginBottom="$4x">
              <Text tag="span" typography="$bodyMedium">
                {reviewerDescription}
              </Text>
            </Text>
          )}
          {reviewerContact && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginBottom="$4x">
              <Text tag="span" typography="$labelMedium">
                Contact:&nbsp;
              </Text>
              <Link
                href={`mailto:${reviewerContact}`}
                aria-label={`Email ${reviewerDisplayName || 'reviewer'}`}
                analyticsName="article-header-reviewer-email"
              >
                {reviewerContact}
              </Link>
            </Text>
          )}
          {authorLinkedIn && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary" marginBottom="$4x">
              <Text tag="span" typography="$labelMedium">
                LinkedIn:&nbsp;
              </Text>
              <Link
                href={getSocialLink('linkedin', authorLinkedIn)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`LinkedIn profile for ${authorDisplayName || 'author'}`}
                analyticsName="article-header-author-linkedin"
              >
                {authorLinkedIn}
              </Link>
            </Text>
          )}
          {reviewerLinkedIn && (
            <Text tag="p" typography="$bodyMedium" color="$contentSecondary">
              <Text tag="span" typography="$labelMedium">
                LinkedIn:&nbsp;
              </Text>
              <Link
                href={getSocialLink('linkedin', reviewerLinkedIn)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`LinkedIn profile for ${reviewerDisplayName || 'reviewer'}`}
                analyticsName="article-header-reviewer-linkedin"
              >
                {reviewerLinkedIn}
              </Link>
            </Text>
          )}
        </Box>
        {headerImgUrl && headerImgAlt && (
          <Box
            id="header-image"
            marginBottom={32}
            $largerThanSM={{
              marginBottom: 0,
              flexGrow: 8,
              flexShrink: 1,
              flexBasis: '50%',
            }}
            minWidth={0}
          >
            <Image
              src={headerImgUrl}
              alt={headerImgAlt}
              borderRadius="roundedSmooth"
              w="100%"
              h="auto"
            />
          </Box>
        )}
        <Box $largerThanSM={{ display: 'none' }}>
          {!props.fields.hideReadingTime && (
            <Text tag="p" typography="$labelMedium" color="$contentSecondary" marginBottom="$8x">
              Reading Time &mdash; {props.readingTimeMinutes} minutes
            </Text>
          )}
          {props.fields.publishDate && (
            <Text tag="p" typography="$labelMedium" color="$contentSecondary" marginBottom="$4x">
              {new Date(props.fields.publishDate + 'T00:00:00').toLocaleDateString('en-us', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
          )}
          {props.fields.actualizationDate && (
            <Text tag="p" typography="$labelMedium" color="$contentSecondary">
              {new Date(props.fields.actualizationDate + 'T00:00:00').toLocaleDateString('en-us', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
          )}
        </Box>
      </Box>
    </>
  );
};
